import { type Component, createSignal, createResource, onMount, onCleanup, Show, For } from 'solid-js'
import { createStore } from 'solid-js/store'
import { A, useNavigate } from '@solidjs/router'
import { AppRoutes } from '../../../shared/app-routes'
import FoldableSection from './FoldableSection'
import PresaleSection from './PresaleSection'
import { inputClass, labelClass, errorClass, SECTIONS, fieldInputClass } from './form-styles'
import { createSectionToggle } from './useSectionToggle'
import {
  validateEventForm,
  defaultEventFormData,
  isMeetAndGreetType,
  type EventFormData
} from '../domain/event-form.schema'
import LocationMap from './LocationMap'
import DateTimeInput from '../../../components/shared/components/DateTimeInput'
import TimezoneSelect from '../../../components/shared/components/TimezoneSelect'
import ImageUpload from '../../../components/shared/components/ImageUpload'
import { useAdminEvents } from '../../../shared/providers/coris/coris-admin-events.provider'
import { useNotification } from '../../../shared/notifications'
import { formDataToPayload } from '../domain/event-form-to-payload'

const requiredMark = () => <span class="text-red-400 ml-0.5">*</span>

/** Maps an error field to the foldable section it lives in, so a collapsed section can be opened to reveal its error. */
const FIELD_SECTION: Record<string, string> = {
  ticketButtonText: 'ticket_sales',
  ticketUrl: 'ticket_sales',
  rsvpButtonText: 'early_access',
  presaleCode: 'presale',
  vipButtonText: 'vip',
  vipUrl: 'vip',
  meetButtonText: 'addons',
  meetAndGreetStart: 'addons',
  meetAndGreetEnd: 'addons'
}

interface EventFormProps {
  readonly mode?: 'create' | 'edit'
  readonly eventId?: number
  readonly initialValues?: Partial<EventFormData>
}

const EventForm: Component<EventFormProps> = (props) => {
  const { createEvent, updateEvent, uploadEventImage, getPresaleCodePools, getEventCategories, unassignPoolFromEvent } =
    useAdminEvents()
  const notification = useNotification()
  const navigate = useNavigate()
  const iv = props.initialValues ?? {}
  const {
    isEnabled: isSectionEnabled,
    toggle: toggleSection,
    setActive: setActiveSection,
    activeSection
  } = createSectionToggle(iv)

  // Form state — single store derived from Zod schema type
  const [form, setForm] = createStore<EventFormData>({ ...defaultEventFormData(), ...iv })
  const setField = <K extends keyof EventFormData>(key: K, value: EventFormData[K]): void => {
    setForm(key, value)
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Fetch event categories for this artist (needed for eventCategoryId on save)
  const [categories] = createResource(getEventCategories)

  // Non-schema UI state
  const [imageFile, setImageFile] = createSignal<File | null>(null)
  const [hasLinkedPools, setHasLinkedPools] = createSignal(false)

  // FIXME: Timezone mismatch detection — disabled, to be re-enabled as a standalone component

  let isClickScrolling = false

  const scrollToSection = (id: string): void => {
    isClickScrolling = true
    setActiveSection(id)
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setTimeout(() => {
      isClickScrolling = false
    }, 800)
  }

  // Track active section by scroll position
  onMount(() => {
    const sectionIds: readonly string[] = SECTIONS.map((s) => s.id)
    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling) return
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace('section-', '')
            if (sectionIds.includes(id)) {
              setActiveSection(id)
            }
            break
          }
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    )

    for (const id of sectionIds) {
      const el = document.getElementById(`section-${id}`)
      if (el) observer.observe(el)
    }

    onCleanup(() => observer.disconnect())
  })

  const [errors, setErrors] = createSignal<Record<string, string>>({})

  const fieldError = (field: string): string | undefined => errors()[field]
  const inputCls = (field: string): string => fieldInputClass(field, errors())

  const collectFormData = (): EventFormData => ({
    ...form,
    ticketSalesEnabled: isSectionEnabled('ticket_sales'),
    earlyAccessEnabled: isSectionEnabled('early_access'),
    presaleEnabled: isSectionEnabled('presale'),
    vipEnabled: isSectionEnabled('vip'),
    addonsEnabled: isSectionEnabled('addons'),
    hasPresalePools: hasLinkedPools()
  })

  const handleSave = (): void => {
    const data = collectFormData()
    const validationErrors = validateEventForm(data)
    if (validationErrors) {
      setErrors(validationErrors)
      const errorKeys = Object.keys(validationErrors)

      // Open any collapsed section that holds an error — otherwise the message renders hidden inside it.
      const sectionsToOpen = new Set(
        errorKeys.map((k) => FIELD_SECTION[k]).filter((id): id is string => !!id && !isSectionEnabled(id))
      )
      sectionsToOpen.forEach((id) => toggleSection(id, true))

      // A presale code requires an active ticket-sales link — explain why, in plain language for non-technical artists.
      if (
        data.presaleEnabled &&
        data.presaleCode &&
        (validationErrors.ticketUrl || validationErrors.ticketButtonText)
      ) {
        notification.warn(
          <span>
            A presale code needs a ticket link so fans have somewhere to go. Fill in the Link URL and button text under{' '}
            <strong>Ticket sales</strong> below. If your ticket page isn’t ready yet, you can use a temporary link for
            now — like your band’s website — and update it later.
          </span>,
          { sticky: true }
        )
      }

      // Scroll to the first error; wait for the section expand animation (200ms) when one just opened.
      // Any error whose field has no anchor in the DOM is raised as a notification instead —
      // otherwise Save/Update looks dead: nothing scrolls, nothing turns red, nothing is said.
      const surfaceErrors = (): void => {
        const anchors = new Map(errorKeys.map((k) => [k, document.querySelector(`[data-field="${k}"]`)] as const))
        const orphaned = errorKeys.filter((k) => !anchors.get(k))
        if (orphaned.length > 0) {
          notification.error(<span>{orphaned.map((k) => validationErrors[k]).join(' ')}</span>)
        }
        const firstAnchored = errorKeys.find((k) => anchors.get(k))
        if (firstAnchored) {
          anchors.get(firstAnchored)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
      if (sectionsToOpen.size > 0) setTimeout(surfaceErrors, 250)
      else surfaceErrors()
      return
    }
    setErrors({})

    const categoryId = categories()?.[0]?.id
    if (!categoryId) {
      notification.error(<span>Event categories not loaded yet. Please try again.</span>)
      return
    }
    const payload = formDataToPayload(data, categoryId)
    const isEdit = props.mode === 'edit' && props.eventId

    notification.progress(<span>{isEdit ? 'Updating' : 'Creating'} event...</span>, {
      task: async () => {
        const event = isEdit ? await updateEvent(props.eventId!, payload) : await createEvent(payload)
        // Upload image if a new file was selected (two-stage: save event, then upload image)
        const file = imageFile()
        if (file && event?.id) {
          await uploadEventImage(event.id, file)
        }
        return event
      },
      onSuccess: () => {
        navigate(AppRoutes.AdminEvents())
        return <span>Event {isEdit ? 'updated' : 'created'} successfully</span>
      },
      onError: (err) => (
        <span>
          Failed to {isEdit ? 'update' : 'create'} event: {err instanceof Error ? err.message : 'Unknown error'}
        </span>
      )
    })
  }

  const canSave = () => form.eventName.trim() !== ''

  return (
    <div class="flex w-full flex-col min-h-full p-6">
      <div class="mx-auto max-w-[1280px] w-full flex gap-16">
        {/* Section nav */}
        <div class="w-48 shrink-0">
          <nav class="sticky top-0 flex flex-col gap-0.5 pt-6">
            <For each={SECTIONS}>
              {(section) => (
                <button
                  type="button"
                  class="flex items-center gap-2 p-2 rounded-md transition-colors duration-150 cursor-pointer text-left w-full"
                  classList={{
                    'bg-gray-50': activeSection() === section.id,
                    'hover:bg-gray-50': activeSection() !== section.id
                  }}
                  onClick={() => {
                    if (section.toggleable && !isSectionEnabled(section.id)) {
                      toggleSection(section.id, true)
                    } else {
                      scrollToSection(section.id)
                    }
                  }}
                >
                  <span
                    class="flex-1 text-sm font-semibold transition-colors duration-150 truncate"
                    classList={{
                      'text-primary-800':
                        activeSection() === section.id && (!section.toggleable || isSectionEnabled(section.id)),
                      'text-gray-600':
                        activeSection() !== section.id && (!section.toggleable || isSectionEnabled(section.id)),
                      'text-gray-400': section.toggleable && !isSectionEnabled(section.id)
                    }}
                  >
                    {section.label}
                  </span>
                </button>
              )}
            </For>
          </nav>
        </div>

        {/* Form cards */}
        <div class="flex-1 max-w-[704px] flex flex-col gap-4">
          {/* General */}
          <div id="section-general" class="bg-white rounded-lg border border-gray-200 px-6 py-5 flex flex-col gap-6">
            <h2 class="text-xl font-bold text-gray-900">General</h2>
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-2.5" data-field="eventName">
                <label class={labelClass}>Event name{requiredMark()}</label>
                <input
                  type="text"
                  class={inputCls('eventName')}
                  value={form.eventName}
                  onInput={(e) => setField('eventName', e.currentTarget.value)}
                  placeholder="Enter event name"
                />
                <Show when={fieldError('eventName')}>
                  <p class={errorClass}>{fieldError('eventName')}</p>
                </Show>
              </div>

              <div class="flex flex-col gap-2.5" data-field="timezone">
                <label class={labelClass}>Timezone{requiredMark()}</label>
                <TimezoneSelect
                  value={form.timezone}
                  onValueChange={(v) => setField('timezone', v)}
                  hasError={!!fieldError('timezone')}
                />
                <Show when={fieldError('timezone')}>
                  <p class={errorClass}>{fieldError('timezone')}</p>
                </Show>
              </div>

              <div class="flex flex-col gap-2.5" data-field="eventDate">
                <label class={labelClass}>Event date{requiredMark()}</label>
                <DateTimeInput
                  value={form.eventDate}
                  onValueChange={(v) => setField('eventDate', v ?? '')}
                  placeholder="Select event date"
                  hasError={!!fieldError('eventDate')}
                />
                <Show when={fieldError('eventDate')}>
                  <p class={errorClass}>{fieldError('eventDate')}</p>
                </Show>
              </div>

              <div class="flex gap-4">
                <div class="flex-1 flex flex-col gap-2.5">
                  <label class={labelClass}>Display until</label>
                  <DateTimeInput
                    value={form.displayUntil}
                    onValueChange={(v) => setField('displayUntil', v ?? '')}
                    placeholder="Select date & time"
                    mode="datetime"
                  />
                </div>
                <div class="flex-1 flex flex-col gap-2.5">
                  <label class={labelClass}>Publish at</label>
                  <DateTimeInput
                    value={form.publishAt}
                    onValueChange={(v) => setField('publishAt', v ?? '')}
                    placeholder="Select date & time"
                    mode="datetime"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-2.5">
                <label class={labelClass}>Image</label>
                <ImageUpload value={iv.imageUrl} onFileChange={(file) => setImageFile(file)} />
              </div>
            </div>
          </div>

          {/* Location */}
          <div id="section-location" class="bg-white rounded-lg border border-gray-200 px-6 py-5 flex flex-col gap-6">
            <h2 class="text-xl font-bold text-gray-900">Location</h2>

            {/* Map with embedded search */}
            <div class="w-full rounded-lg overflow-hidden border border-gray-200" style={{ height: '280px' }}>
              <LocationMap
                onLocationPicked={(parsed) => {
                  if (parsed.venueName) setField('venueName', parsed.venueName)
                  if (parsed.addressLine1) setField('addressLine1', parsed.addressLine1)
                  if (parsed.city) setField('city', parsed.city)
                  if (parsed.state) setField('state', parsed.state)
                  if (parsed.zipCode) setField('zipCode', parsed.zipCode)
                  if (parsed.country) setField('country', parsed.country)
                }}
              />
            </div>

            {/* Form fields — editable context */}
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-2.5" data-field="venueName">
                <label class={labelClass}>Venue name{requiredMark()}</label>
                <input
                  type="text"
                  class={inputCls('venueName')}
                  value={form.venueName}
                  onInput={(e) => setField('venueName', e.currentTarget.value)}
                  placeholder="Enter a venue name"
                />
                <Show when={fieldError('venueName')}>
                  <p class={errorClass}>{fieldError('venueName')}</p>
                </Show>
              </div>

              <div class="flex flex-col gap-2.5" data-field="addressLine1">
                <label class={labelClass}>Address line 1</label>
                <input
                  type="text"
                  class={inputCls('addressLine1')}
                  value={form.addressLine1}
                  onInput={(e) => setField('addressLine1', e.currentTarget.value)}
                  placeholder="Enter address"
                />
                <Show when={fieldError('addressLine1')}>
                  <p class={errorClass}>{fieldError('addressLine1')}</p>
                </Show>
              </div>

              <div class="flex flex-col gap-2.5">
                <label class={labelClass}>Address line 2 (optional)</label>
                <input
                  type="text"
                  class={inputClass}
                  value={form.addressLine2}
                  onInput={(e) => setField('addressLine2', e.currentTarget.value)}
                  placeholder="Apartment, suite, etc."
                />
              </div>

              <div class="flex gap-4">
                <div class="flex-1 flex flex-col gap-2.5" data-field="city">
                  <label class={labelClass}>City{requiredMark()}</label>
                  <input
                    type="text"
                    class={inputCls('city')}
                    value={form.city}
                    onInput={(e) => setField('city', e.currentTarget.value)}
                    placeholder="Enter city"
                  />
                  <Show when={fieldError('city')}>
                    <p class={errorClass}>{fieldError('city')}</p>
                  </Show>
                </div>
                <div class="flex-1 flex flex-col gap-2.5" data-field="state">
                  <label class={labelClass}>State{requiredMark()}</label>
                  <input
                    type="text"
                    class={inputCls('state')}
                    value={form.state}
                    onInput={(e) => setField('state', e.currentTarget.value)}
                    placeholder="Enter state"
                  />
                  <Show when={fieldError('state')}>
                    <p class={errorClass}>{fieldError('state')}</p>
                  </Show>
                </div>
              </div>

              <div class="flex gap-4">
                <div class="flex-1 flex flex-col gap-2.5">
                  <label class={labelClass}>Zip Code</label>
                  <input
                    type="text"
                    class={inputCls('zipCode')}
                    value={form.zipCode}
                    onInput={(e) => setField('zipCode', e.currentTarget.value)}
                    placeholder="Enter zip code"
                  />
                  <Show when={fieldError('zipCode')}>
                    <p class={errorClass}>{fieldError('zipCode')}</p>
                  </Show>
                </div>
                <div class="flex-1 flex flex-col gap-2.5" data-field="country">
                  <label class={labelClass}>Country{requiredMark()}</label>
                  <input
                    type="text"
                    class={inputCls('country')}
                    value={form.country}
                    onInput={(e) => setField('country', e.currentTarget.value)}
                    placeholder="Enter country"
                  />
                  <Show when={fieldError('country')}>
                    <p class={errorClass}>{fieldError('country')}</p>
                  </Show>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket sales */}
          <FoldableSection
            id="ticket_sales"
            title="Ticket sales"
            tooltip="Toggle whether to display a ticket sales link for this event."
            enabled={() => isSectionEnabled('ticket_sales')}
            onToggle={(v) => toggleSection('ticket_sales', v)}
          >
            <div class="flex flex-col gap-2.5" data-field="ticketButtonText">
              <label class={labelClass}>Link URL Button Text{requiredMark()}</label>
              <input
                type="text"
                class={inputCls('ticketButtonText')}
                value={form.ticketButtonText}
                onInput={(e) => setField('ticketButtonText', e.currentTarget.value)}
                placeholder="Text to display on the button"
              />
              <Show when={fieldError('ticketButtonText')}>
                <p class={errorClass}>{fieldError('ticketButtonText')}</p>
              </Show>
            </div>

            <div class="flex flex-col gap-2.5" data-field="ticketUrl">
              <label class={labelClass}>Link URL{requiredMark()}</label>
              <input
                type="url"
                class={inputCls('ticketUrl')}
                value={form.ticketUrl}
                onInput={(e) => setField('ticketUrl', e.currentTarget.value)}
                placeholder="Ticket URL shown to fans"
              />
              <Show when={fieldError('ticketUrl')}>
                <p class={errorClass}>{fieldError('ticketUrl')}</p>
              </Show>
            </div>
          </FoldableSection>

          {/* Early Access */}
          <FoldableSection
            id="early_access"
            title="Early Access"
            tooltip="Toggle whether this event offers Early Access."
            enabled={() => isSectionEnabled('early_access')}
            onToggle={(v) => toggleSection('early_access', v)}
          >
            <div class="flex flex-col gap-2.5" data-field="rsvpButtonText">
              <label class={labelClass}>Early Access button text{requiredMark()}</label>
              <input
                type="text"
                class={inputCls('rsvpButtonText')}
                value={form.rsvpButtonText}
                onInput={(e) => setField('rsvpButtonText', e.currentTarget.value)}
                placeholder="Early Access"
              />
              <Show when={fieldError('rsvpButtonText')}>
                <p class={errorClass}>{fieldError('rsvpButtonText')}</p>
              </Show>
            </div>
            <div class="flex flex-col gap-2.5">
              <label class={labelClass}>Capacity</label>
              <input
                type="number"
                min="0"
                class={inputClass}
                value={form.rsvpCapacity}
                onInput={(e) => setField('rsvpCapacity', e.currentTarget.value)}
                placeholder="Number of fans allowed"
              />
            </div>
            <div class="flex gap-4">
              <div class="flex-1 flex flex-col gap-2.5">
                <label class={labelClass}>Early Access start</label>
                <DateTimeInput
                  value={form.rsvpStart}
                  onValueChange={(v) => setField('rsvpStart', v ?? '')}
                  mode="datetime"
                  placeholder="Select date & time"
                />
              </div>
              <div class="flex-1 flex flex-col gap-2.5">
                <label class={labelClass}>Early Access end</label>
                <DateTimeInput
                  value={form.rsvpEnd}
                  onValueChange={(v) => setField('rsvpEnd', v ?? '')}
                  mode="datetime"
                  placeholder="Select date & time"
                />
              </div>
            </div>
            <div class="flex flex-col gap-2.5">
              <label class={labelClass}>Early Access info text</label>
              <input
                type="text"
                class={inputClass}
                value={form.rsvpInfo}
                onInput={(e) => setField('rsvpInfo', e.currentTarget.value)}
                placeholder="Custom text shown when a fan is about to request Early Access"
              />
            </div>
            <div class="flex flex-col gap-2.5">
              <label class={labelClass}>Early Access leave info text</label>
              <input
                type="text"
                class={inputClass}
                value={form.rsvpLeaveInfo}
                onInput={(e) => setField('rsvpLeaveInfo', e.currentTarget.value)}
                placeholder="Custom text shown when a fan removes their Early Access"
              />
            </div>
          </FoldableSection>

          {/* Ticket pre-sales */}
          <FoldableSection
            id="presale"
            title="Ticket pre-sales"
            tooltip="Toggle whether to show a presale code to fans."
            enabled={() => isSectionEnabled('presale')}
            onToggle={(v) => toggleSection('presale', v)}
          >
            <PresaleSection
              form={form}
              setField={setField}
              inputCls={inputCls}
              fieldError={fieldError}
              getPresaleCodePools={getPresaleCodePools}
              unassignPoolFromEvent={unassignPoolFromEvent}
              eventId={props.eventId}
              onLinkedPoolsFound={() => {
                toggleSection('presale', true)
                setHasLinkedPools(true)
              }}
            />
          </FoldableSection>

          {/* VIP ticket sales */}
          <FoldableSection
            id="vip"
            title="VIP ticket sales"
            tooltip="Toggle whether to display a VIP ticket sales link for this event."
            enabled={() => isSectionEnabled('vip')}
            onToggle={(v) => toggleSection('vip', v)}
          >
            <div class="flex flex-col gap-2.5" data-field="vipButtonText">
              <label class={labelClass}>VIP button text{requiredMark()}</label>
              <input
                type="text"
                class={inputCls('vipButtonText')}
                value={form.vipButtonText}
                onInput={(e) => setField('vipButtonText', e.currentTarget.value)}
                placeholder="VIP"
              />
              <Show when={fieldError('vipButtonText')}>
                <p class={errorClass}>{fieldError('vipButtonText')}</p>
              </Show>
            </div>
            <div class="flex flex-col gap-2.5" data-field="vipUrl">
              <label class={labelClass}>VIP URL{requiredMark()}</label>
              <input
                type="url"
                class={inputCls('vipUrl')}
                value={form.vipUrl}
                onInput={(e) => setField('vipUrl', e.currentTarget.value)}
                placeholder="Link to VIP ticket sales"
              />
              <Show when={fieldError('vipUrl')}>
                <p class={errorClass}>{fieldError('vipUrl')}</p>
              </Show>
            </div>
          </FoldableSection>

          {/* Event Add-On */}
          <FoldableSection
            id="addons"
            title="Event Add-On"
            tooltip="Toggle to offer Meet & Greet for this event. Set the availability window and customize how it appears to your fans."
            enabled={() => isSectionEnabled('addons')}
            onToggle={(v) => toggleSection('addons', v)}
          >
            <div class="flex flex-col gap-2.5">
              <label class={labelClass}>Type</label>
              <select
                class={inputClass}
                value={form.meetAndGreetType}
                onChange={(e) => {
                  const type = e.currentTarget.value
                  if (!isMeetAndGreetType(type)) return
                  setField('meetAndGreetType', type)
                  if (type === 'meetAndGreet') {
                    setField('meetButtonText', 'Meet and Greet')
                  }
                }}
              >
                <option value="meetAndGreet">Meet and Greet</option>
                <option value="custom">Custom</option>
              </select>
              <p class="text-xs text-gray-500">
                Choose "Meet and Greet" for a ready-to-go setup, or "Custom" to personalize every detail.
              </p>
            </div>
            <div class="flex flex-col gap-2.5" data-field="meetButtonText">
              <label class={labelClass}>Button text{requiredMark()}</label>
              <input
                type="text"
                class={inputCls('meetButtonText')}
                value={form.meetButtonText}
                onInput={(e) => setField('meetButtonText', e.currentTarget.value)}
                placeholder="Sound Check, Meet and Greet, etc."
              />
              <Show when={fieldError('meetButtonText')}>
                <p class={errorClass}>{fieldError('meetButtonText')}</p>
              </Show>
            </div>
            <div class="flex flex-col gap-2.5">
              <label class={labelClass}>Capacity (optional)</label>
              <input
                type="number"
                min="0"
                class={inputClass}
                value={form.meetAndGreetCapacity}
                onInput={(e) => setField('meetAndGreetCapacity', e.currentTarget.value)}
                placeholder="Leave empty for unlimited"
              />
            </div>
            <div class="flex gap-4">
              <div class="flex-1 flex flex-col gap-2.5" data-field="meetAndGreetStart">
                <label class={labelClass}>Available from{requiredMark()}</label>
                <DateTimeInput
                  value={form.meetAndGreetStart}
                  onValueChange={(v) => setField('meetAndGreetStart', v ?? '')}
                  mode="datetime"
                  placeholder="Select date & time"
                  hasError={!!fieldError('meetAndGreetStart')}
                />
                <Show when={fieldError('meetAndGreetStart')}>
                  <p class={errorClass}>{fieldError('meetAndGreetStart')}</p>
                </Show>
              </div>
              <div class="flex-1 flex flex-col gap-2.5" data-field="meetAndGreetEnd">
                <label class={labelClass}>Available until{requiredMark()}</label>
                <DateTimeInput
                  value={form.meetAndGreetEnd}
                  onValueChange={(v) => setField('meetAndGreetEnd', v ?? '')}
                  mode="datetime"
                  placeholder="Select date & time"
                  hasError={!!fieldError('meetAndGreetEnd')}
                />
                <Show when={fieldError('meetAndGreetEnd')}>
                  <p class={errorClass}>{fieldError('meetAndGreetEnd')}</p>
                </Show>
              </div>
            </div>
            <Show when={form.meetAndGreetType === 'custom'}>
              <div class="flex flex-col gap-2.5">
                <label class={labelClass}>Join dialog text</label>
                <input
                  type="text"
                  class={inputClass}
                  value={form.meetAndGreetJoinText}
                  onInput={(e) => setField('meetAndGreetJoinText', e.currentTarget.value)}
                  placeholder="Join the Meet and Greet"
                />
              </div>
              <div class="flex flex-col gap-2.5">
                <label class={labelClass}>Unjoin dialog text</label>
                <input
                  type="text"
                  class={inputClass}
                  value={form.meetAndGreetLeaveText}
                  onInput={(e) => setField('meetAndGreetLeaveText', e.currentTarget.value)}
                  placeholder="Leave the Meet and Greet"
                />
              </div>
            </Show>
          </FoldableSection>

          {/* Footer actions */}
          <div class="flex justify-end gap-4 py-4">
            <A
              href={AppRoutes.AdminEvents()}
              class="min-w-24 px-3.5 py-2.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 text-center"
            >
              Cancel
            </A>
            <button
              type="button"
              class="min-w-24 px-3.5 py-2.5 rounded-md text-sm font-semibold text-center transition-colors duration-150"
              classList={{
                'bg-primary-600 text-white hover:bg-primary-700 shadow-sm': canSave(),
                'bg-primary-600 text-white opacity-40 cursor-not-allowed': !canSave()
              }}
              disabled={!canSave()}
              onClick={handleSave}
            >
              {props.mode === 'edit' ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EventForm
